import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import {
  GOOGLE_WORKSPACE_REFERRAL_URL,
  trackWorkspaceReferralClick,
} from "../lib/partners";

const STORAGE_KEY = "oly.workspaceBannerDismissedAt";
// Re-show the banner 30 days after dismissal so we don't lose long-term users.
const REAPPEAR_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Dismissible referral banner shown at the top of Dashboard.
 * Auto-hides for 30 days after the user closes it, then reappears — matches
 * how Google/Notion/etc. handle upgrade nudges without being annoying.
 */
export default function WorkspaceBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setVisible(true); return; }
      const dismissedAt = parseInt(raw, 10);
      if (Number.isNaN(dismissedAt)) { setVisible(true); return; }
      if (Date.now() - dismissedAt > REAPPEAR_MS) setVisible(true);
    } catch (_) { setVisible(true); }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      className="relative rounded-2xl border border-[#E2E8F0] bg-gradient-to-r from-[#0F172A] to-[#1e293b] text-white p-5 mb-6 overflow-hidden"
      data-testid="workspace-banner"
    >
      {/* Decorative gradient blob */}
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#F26953]/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-center gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-5 w-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-white">
            Need Google Docs, Meet, Drive for your team?
          </div>
          <div className="text-xs text-slate-300 mt-0.5">
            Try Google Workspace free for 14 days. Keep oly Inbox for forwarding — they work great together.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={GOOGLE_WORKSPACE_REFERRAL_URL}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={() => trackWorkspaceReferralClick("dashboard-banner")}
            data-testid="workspace-banner-cta"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-[#0F172A] bg-white hover:bg-slate-100 rounded-lg"
          >
            Try free
            <ArrowRight className="h-3 w-3" />
          </a>
          <Link
            to="/workspace-upgrade"
            data-testid="workspace-banner-details"
            className="text-xs text-slate-300 hover:text-white px-2 py-2"
          >
            Details
          </Link>
          <button
            onClick={dismiss}
            data-testid="workspace-banner-dismiss"
            aria-label="Dismiss banner for 30 days"
            className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
